import { useState, useEffect } from 'react'
import { ScrollReveal } from '@/components/ui/scroll-reveal'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion'
import { getFaqs, Faq } from '@/services/content'
import useLanguageStore from '@/stores/languageStore'
import { HelpCircle } from 'lucide-react'

export const FaqSection = () => {
  const { currentLocale } = useLanguageStore()
  const [faqs, setFaqs] = useState<Faq[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFaqs = async () => {
      setLoading(true)
      const { data } = await getFaqs(currentLocale)
      if (data) setFaqs(data)
      setLoading(false)
    }
    fetchFaqs()
  }, [currentLocale])

  if (!loading && faqs.length === 0) return null

  return (
    <section className="py-24 bg-white">
      <div className="container px-4 md:px-6 max-w-4xl mx-auto">
        <ScrollReveal className="text-center mb-16">
          <div className="inline-flex items-center justify-center space-x-2 mb-6 px-4 py-1.5 rounded-full bg-slate-100 text-slate-700 font-medium text-sm">
            <HelpCircle size={16} />
            <span>FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            {currentLocale === 'pt'
              ? 'Perguntas Frequentes'
              : 'Frequently Asked Questions'}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {currentLocale === 'pt'
              ? 'Esclareça as suas dúvidas sobre como trabalhamos e o que esperar.'
              : 'Clear up your doubts about how we work and what to expect.'}
          </p>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-20 bg-slate-50 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="border border-slate-200 rounded-lg px-6 bg-slate-50 data-[state=open]:bg-white data-[state=open]:border-primary/20 data-[state=open]:shadow-sm transition-all duration-300"
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-900 text-lg py-6 hover:no-underline hover:text-primary transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed text-base pb-6 pt-2 prose prose-slate max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ScrollReveal>
      </div>
    </section>
  )
}
